'use client';

import { formatPrice } from '@lichtblick/localization';
import { AccentText } from '@lichtblick/photon/atoms/accent-text/accent-text';
import { Radio } from '@lichtblick/photon/atoms/radio';
import { Text } from '@lichtblick/photon/atoms/text/text';
import { useSearchParams } from 'next/navigation';
import { useMemo } from 'react';
import { getLoyaltyBonusInEur, getProductType } from '@/components/card/card-utils';
import { Card } from '@/components/electricity-tariffs/card';
import styles from '@/components/electricity-tariffs/card.module.scss';
import { PriceBreakdownList } from '@/components/price-breakdown/price-breakdown-list';
import { PriceBreakdownRow } from '@/components/price-breakdown/price-breakdown-row';
import { ElectricityTariffDetailsLayer } from '@/components/tariff-details-layer/electricity-tariff-details-layer';
import { VarioTariffDetailsLayer } from '@/components/tariff-details-layer/vario-tariff-details-layer';
import { getCurrentMonth } from '@/helpers/date';
import type { TariffsQuery } from '@/types/generated';
import { benefits, benefitsColor, CardBenefitsRow } from './benefits';

type ElectricityCardProps = {
  cardIndex: number;
  isComboCheckout: boolean;
  product: TariffsQuery['tariffs'][number];
  isSelected: boolean;
};

export const ElectricityCard: React.FC<ElectricityCardProps> = ({
  cardIndex,
  isComboCheckout,
  product,
  isSelected,
}) => {
  const isVario = product?.productCode?.includes('VARIO');
  const productType = getProductType(product.productCode ?? '');
  const productBenefits = useMemo(() => benefits[productType](product), [productType, product]);
  const recommendation = productType === 'relax';

  const loyaltyBonusInEur = getLoyaltyBonusInEur(product);
  const instantBonusInEur = product.bonus?.instantBonusInEur;
  const hasBonus = Boolean(loyaltyBonusInEur) || Boolean(instantBonusInEur);
  const productName = product.name ?? `${cardIndex}`;
  const contractPeriod = `${product.contractPeriod?.value} Monate`;

  const searchParams = useSearchParams();

  const handleSelection = () => {
    if (!product.productCode) {
      return;
    }

    const params = new URLSearchParams(searchParams.toString());
    params.set('productCode', product.productCode);

    history.replaceState(null, '', `?${params.toString()}`);
  };

  return (
    <Card
      aria-label={`Tarif ${product.name}`}
      data-testid="tariff-card-electricity"
      data-testproduct={product.productCode}
      productName={productName}
    >
      {recommendation && (
        <Text bold size="s" className={styles.chip}>
          Stark nachgefragt!
        </Text>
      )}

      <div className={styles['content-wrapper']}>
        <Radio
          className={styles.radio}
          onChange={handleSelection}
          name="tariff-card"
          id={productName}
          value={productName}
          checked={isSelected}
        />

        <Text bold size="l" className={styles['product-name']}>
          {product.name}
        </Text>

        <ul className={styles.benefits}>
          {productBenefits.marketingClaim && (
            <CardBenefitsRow
              color={benefitsColor.marketingClaim}
              text={productBenefits.marketingClaim}
              placeholder={false}
            />
          )}

          <li>
            <ul className={styles['benefits-group']}>
              {productBenefits.bonus && (
                <CardBenefitsRow color={benefitsColor.bonus} text={productBenefits.bonus} placeholder={false} />
              )}
              {productBenefits.priceGuarantee && (
                <CardBenefitsRow
                  color={benefitsColor.priceGuarantee}
                  text={productBenefits.priceGuarantee}
                  placeholder={false}
                />
              )}
            </ul>
          </li>
        </ul>

        <PriceBreakdownList className={styles['price-breakdown']}>
          {!isVario && <PriceBreakdownRow label="Laufzeit" value={contractPeriod} />}
          <PriceBreakdownRow
            label={isVario ? `Arbeitspreis im ${getCurrentMonth()}¹` : 'Arbeitspreis'}
            value={formatPrice(product.workPrice, 'ct/kWh')}
          />
          <PriceBreakdownRow label="Monatl. Grundpreis" value={formatPrice(product.monthlyBasePrice)} />
        </PriceBreakdownList>

        <span className={styles.price}>
          <AccentText className={styles['price-value']}>
            {formatPrice(product.monthlyPriceWithBonus, '€', 0)}
          </AccentText>

          <Text size="xs">
            {isVario && `Abschlag im ${getCurrentMonth()}¹`}
            {!isVario && (hasBonus ? <>ø Kosten pro Monat</> : 'Abschlag pro Monat')}
          </Text>
        </span>

        <span className={styles.button}>
          {!isVario && (
            <ElectricityTariffDetailsLayer
              renderLinkAs="button"
              index={cardIndex}
              isComboCheckout={isComboCheckout}
              product={product}
            />
          )}

          {isVario && (
            <VarioTariffDetailsLayer
              renderLinkAs="button"
              index={cardIndex}
              isComboCheckout={isComboCheckout}
              product={product}
            />
          )}
        </span>
      </div>
    </Card>
  );
};
